import { useState } from 'react';
import { channelWa } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../i18n/I18nProvider';
import { MoveFromApp } from './MoveFromApp';

/*
 * One component for every refusal from WhatsApp.
 *
 * The attach form, the guided connection, a template submit and the move
 * itself all get the same shape back from the platform: what is wrong, what
 * to do next, and which control to offer. This renders it once, so a person
 * reads the same sentence wherever the refusal appears, and the one refusal
 * that has a way out (the number is signed in to the WhatsApp Business app on
 * a phone) carries the button into the guided move.
 *
 * The wording is translated by code where the catalogue has it and falls
 * back to the platform's own sentence, which is already written for a shop
 * owner. Nothing here ever shows Meta's raw wording.
 */

/** The refusal as it arrives, whichever path refused. */
export interface Refusal {
  code: string;
  message: string;
  next_step?: string;
  action?: string;
  fault?: string;
  retry_after?: string;
  phone_number_id?: string;
  msisdn?: string;
  step?: string;
}

/** Pull the refusal out of an SDK error, or null when it is not one. */
export function refusalFrom(err: unknown): Refusal | null {
  if (channelWa.isMetaRefusal(err)) return err.body.error;
  const api = err as { body?: { error?: Partial<Refusal> } } | null;
  const e = api?.body?.error;
  if (e && typeof e.code === 'string' && typeof e.message === 'string' && (e.next_step || e.action)) {
    return e as Refusal;
  }
  return null;
}

/** The refusals that have a way out through the guided move. */
export function offersMove(r: Refusal): boolean {
  return r.action === 'move_from_app' || r.action === 'verify'
    || r.code === 'NUMBER_IN_WHATSAPP_APP' || r.code === 'NUMBER_NOT_VERIFIED';
}

export function MetaRefusalNotice({
  refusal,
  onClose,
  onMove,
  onConnected,
  inMove = false
}: {
  refusal: Refusal;
  onClose?: () => void;
  /**
   * Where the page would rather open the guided move itself (for instance
   * because this notice sits inside a drawer), it takes the number here and
   * the notice does not open its own.
   */
  onMove?: (number: { msisdn?: string; phone_number_id?: string }) => void;
  /** Called once the guided move opened by this notice has attached the number. */
  onConnected?: () => void;
  /** True inside the guided move itself, where the button would be circular. */
  inMove?: boolean;
}) {
  const t = useT();
  const [moving, setMoving] = useState(false);

  const platform = refusal.fault === 'platform';
  const time = refusal.retry_after ? formatTime(refusal.retry_after) : '';
  const vars = { time, number: refusal.msisdn ?? '' };

  // By code first, then the legacy guided-connection strings (which cover
  // the same codes from before the shared shape), then the platform's own
  // sentence, which is written for the customer already.
  const message = t(`numbers.refusal.${refusal.code}.message`,
    t(`numbers.connect.error.${refusal.code}`, refusal.message, vars), vars);
  const next = refusal.next_step
    ? t(`numbers.refusal.${refusal.code}.next`, refusal.next_step, vars)
    : '';

  const move = !inMove && offersMove(refusal);

  return (
    <>
      <Alert
        color={platform ? 'warning' : 'error'}
        role="alert"
        title={platform ? t('numbers.refusal.platformTitle') : t('numbers.refusal.title')}
        withCloseButton={!!onClose}
        onClose={onClose}
      >
        <Stack gap={6}>
          <Text role="bodySmall">{message}</Text>
          {next && (
            <Text role="bodySmall" c="var(--rr-color-text-muted)">
              <strong>{t('numbers.refusal.nextStep')}:</strong> {next}
            </Text>
          )}
          {move && (
            <Group gap="xs" wrap="wrap" style={{ paddingTop: 4 }}>
              <Button
                size="xs"
                onClick={() => {
                  if (onMove) onMove({ msisdn: refusal.msisdn, phone_number_id: refusal.phone_number_id });
                  else setMoving(true);
                }}
              >
                {refusal.action === 'verify' || refusal.code === 'NUMBER_NOT_VERIFIED'
                  ? t('numbers.refusal.verifyAction')
                  : t('numbers.refusal.moveAction')}
              </Button>
            </Group>
          )}
        </Stack>
      </Alert>

      {move && !onMove && (
        <MoveFromApp
          opened={moving}
          onClose={() => setMoving(false)}
          initialMsisdn={refusal.msisdn}
          initialPhoneNumberId={refusal.phone_number_id}
          onConnected={() => { onConnected?.(); onClose?.(); }}
        />
      )}
    </>
  );
}

/** "14:05" in the viewer's own clock, because a UTC time is nobody's. */
export function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
